import { useEffect, useRef, useState } from "react";

export interface CountdownState {
  isExpired: boolean;
  timeRemaining: number; // in milliseconds
  minutes: number;
  seconds: number;
  formattedTime: string; // "MM:SS"
}

/**
 * Counts down to a quote's expiry.
 *
 * The only state is a ticking clock; everything the caller reads is derived from it during render. That is
 * deliberate — storing `timeRemaining` and writing it from an effect meant the first render always reported
 * `00:00` / `isExpired: true` for one frame before the effect corrected it, which made a fresh quote flash as
 * expired.
 *
 * @param expiresAt - ISO timestamp of when the quote expires. Null/undefined disables the countdown.
 * @param onExpire - Called once per `expiresAt`, including immediately when it is already in the past.
 */
export const useQuoteCountdown = (
  expiresAt: string | null | undefined,
  onExpire?: () => void,
): CountdownState => {
  const [now, setNow] = useState<number>(() => Date.now());
  const onExpireRef = useRef(onExpire);
  /** The `expiresAt` we already fired for, so a re-render cannot fire the callback twice. */
  const firedForRef = useRef<string | null>(null);

  useEffect(() => {
    onExpireRef.current = onExpire;
  }, [onExpire]);

  const expirationTime = expiresAt ? new Date(expiresAt).getTime() : null;
  const timeRemaining = expirationTime === null ? 0 : Math.max(0, expirationTime - now);
  const isExpired = expirationTime !== null && timeRemaining === 0;

  // One self-clearing interval per target. `now` is never reset when the target changes — it is a clock, and
  // the remaining time is recomputed from whatever `expiresAt` currently is.
  useEffect(() => {
    if (expirationTime === null || Date.now() >= expirationTime) return;

    const intervalId = window.setInterval(() => {
      const tick = Date.now();
      setNow(tick);
      if (tick >= expirationTime) window.clearInterval(intervalId);
    }, 1000);

    return () => window.clearInterval(intervalId);
  }, [expirationTime]);

  useEffect(() => {
    if (!expiresAt || !isExpired || firedForRef.current === expiresAt) return;
    firedForRef.current = expiresAt;
    onExpireRef.current?.();
  }, [expiresAt, isExpired]);

  const totalSeconds = Math.floor(timeRemaining / 1000);
  const minutes = Math.floor(totalSeconds / 60);
  const seconds = totalSeconds % 60;
  const formattedTime = `${minutes.toString().padStart(2, "0")}:${seconds.toString().padStart(2, "0")}`;

  return { isExpired, timeRemaining, minutes, seconds, formattedTime };
};
